import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { FavoritesResponse, GetProjectResponse } from "api/generatedApi";
import { sortCompareString } from "utils/compares";
import { MobileFavoriteRow } from "./mobile-favorites-row";
import { TextIcon } from "components/shared/text-icon/text-icon";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";
import { useDeleteFavorites } from "./hooks/use-delete-favorites";
import { useOrder } from "shared/table/use-order";
import { TableHeaderConfig } from "shared/table/table-header-type";
import { TableHeader } from "shared/table/table-header";

interface Props {
  project: GetProjectResponse;
  favorites: FavoritesResponse[];
}

export const MobileProjectFavorites = (props: Props) => {
  type FavoriteSortableId = "favorite";

  const { favorites, project } = props;
  const { t } = useTranslation();
  const { canSelectFavorites, canDeleteFavorites } = useDashboardRestrictions(project);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set<string>());
  const [checkedAll, setCheckedAll] = useState(false);
  const openDeleteFavoritesDialog = useDeleteFavorites({ project, selectedIds });
  const { direction, getLabelProps } = useOrder<FavoriteSortableId>("favorite");

  const sortedData = useMemo(() => {
    var sortedList = favorites;

    sortedList = [...favorites].sort((a, b) => sortCompareString(direction, a?.text, b?.text));

    return sortedList;
  }, [favorites, direction]);

  const onCheck = useCallback(
    (id: string | undefined) => {
      if (id === undefined) return;
      let ids = new Set(selectedIds);
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      setSelectedIds(ids);
    },
    [selectedIds]
  );

  const onCheckAll = (checked: boolean) => {
    let ids = new Set<string>();
    if (checked) {
      favorites?.forEach((item) => ids.add(item.favoriteItemId ?? ""));
    }
    setSelectedIds(ids);
    setCheckedAll(!checkedAll);
  };

  const isFavoriteChecked = (id: string | undefined): boolean => {
    return selectedIds.has(id ?? "");
  };

  const headerConfig: TableHeaderConfig<FavoriteSortableId>[] = [
    {
      id: "favorite",
      title: t("dashboard.favorits.table.text"),
      sortable: true,
      testid: "dashboard-favorites-header-text",
      alignment: "left",
    },
  ];

  return (
    <Box sx={{ flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      <Typography variant="h5" color="primary.dark" sx={{ pt: 1, pl: 1 }}>
        {t("dashboard.favorits.title")}
      </Typography>
      <Typography variant="body1" color="primary.main" sx={{ pt: 2, pb: 0, pl: 1 }}>
        {t("dashboard.favorits.description")}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <TextIcon>
          <IconButton data-testid="dashboard-favorites-delete-rows" disabled={!canDeleteFavorites(selectedIds)} onClick={openDeleteFavoritesDialog}>
            <DeleteOutlinedIcon />
          </IconButton>
        </TextIcon>
      </Box>
      <TableContainer sx={{ height: "calc(100vh - 240px)", overflowY: "auto", pb: "90px" }} component={Paper}>
        <Table stickyHeader>
          <TableHeader method="checkbox" headerConfig={headerConfig} getLabelProps={getLabelProps} checked={selectedIds.size === favorites.length} checkBoxDisabled={!canSelectFavorites()} onCheckAllProps={onCheckAll} />
          <TableBody>
            {sortedData.map((favorite) => {
              return <MobileFavoriteRow key={favorite.favoriteItemId} project={project} row={favorite} onCheck={onCheck} checked={isFavoriteChecked(favorite.favoriteItemId)} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
